import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../environments/environments';
import { ErrorHandler } from '../utils/error-handler';
import { catchError, throwError, map, firstValueFrom } from 'rxjs';
import {
  adminDropdownFilters,
  adminTableData,
  adminInsertUpdateRequest,
  adminDeleteRequest,
  adminDropdownoptions
} from './interfaces/gas-plant-admin.interfaces';
import { subDevAreaDeleteRequest, subDevAreaDropdownOptions, subDevAreaFilterDropdowns, subDevAreaInsertUpdateRequest, subDevAreaTableData } from './interfaces/sub-dev-area.interface';

@Injectable({
  providedIn: 'root',
})
export class GasPlantAdminService {
  private baseUrl = environment.WebApiURL;
  private readonly oilAndGasAdminExportTabName = 'OilAndGasAdmin';
  private readonly ogSubDevAreaExportTabName = 'OGSubDevAreaMapping';

  constructor(private http: HttpClient) {}

  private get dropdownUrl() {
    return `${this.baseUrl}/api/OilAndGasTakeAway/GetOilAndGasAdmDropDowns`;
  }

  private get tableUrl() {
    return `${this.baseUrl}/api/OilAndGasTakeAway/GetOilAndGasAdmDetails`;
  }

  private get filterDropdownUrl() {
    return `${this.baseUrl}/api/OilAndGasTakeAway/GetOilAndGasAdmFilterDropdowns`;
  }

  private get insertUpdateUrl() {
    return `${this.baseUrl}/api/OilAndGasTakeAway/InsertUpdateOilAndGasAdm`;
  }

  private get deleteUrl() {
    return `${this.baseUrl}/api/OilAndGasTakeAway/RemoveOilAndGasAdmDetails`;
  }

  private get subDevAreaDropDownUrl() {
    return `${this.baseUrl}/api/OilAndGasTakeAway/GetOilAndGasSubDevAreaDropDown`;
  }

  private get subDevAreaTableUrl() {
    return `${this.baseUrl}/api/OilAndGasTakeAway/GetOilGasSubDevAreaDetails`;
  }

  private get subDevAreaInsertUpdateUrl() {
    return `${this.baseUrl}/api/OilAndGasTakeAway/InsertUpdateOilAndGasSubDevArea`;
  }

  private get subDevAreaDeleteUrl() {
    return `${this.baseUrl}/api/OilAndGasTakeAway/RemoveOilGasSubDevArea`;
  }

  private get subDevAreaFilterDropdownUrl() {
      return `${this.baseUrl}/api/OilAndGasTakeAway/GetOilAndGasSubDevAreaFiltersMulti`;
  }

  private get exportUrl() {
    return `${this.baseUrl}/api/FlowNetworkFE/ExportAllTabsData`;
  }

  getSubDevAreaDropdownOptions(filters: subDevAreaDropdownOptions) {
    return this.http.post(this.subDevAreaDropDownUrl, filters).pipe(
      map((response: any) => {
        return response;
      }),
      catchError((error) => {
        ErrorHandler.handleError(error, 'getDropdownOptions');
        return throwError(() => new Error(ErrorHandler.extractApiErrorMessage(error)));
      })
    );
  }

  async exportAllOilAndGasAdminData(): Promise<{ blob: Blob; fileName: string }> {
    return this.exportByTabName(this.oilAndGasAdminExportTabName, 'oil-and-gas-admin.csv');
  }

  async exportAllOilAndGasSubDevAreaData(): Promise<{ blob: Blob; fileName: string }> {
    return this.exportByTabName(this.ogSubDevAreaExportTabName, 'oil-and-gas-sub-dev-area.csv');
  }

  private async exportByTabName(tabName: string, fallbackFileName: string): Promise<{ blob: Blob; fileName: string }> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    const httpResponse = await firstValueFrom(
      this.http.post(this.exportUrl, { tabName }, {
        headers,
        observe: 'response' as const,
        responseType: 'blob' as const,
      })
    );

    const blob = httpResponse.body;
    if (!blob) {
      throw new Error('Export returned an empty file.');
    }

    const contentDisposition = httpResponse.headers.get('content-disposition') || '';
    const contentType = httpResponse.headers.get('content-type') || blob.type || '';
    const fileNameMatch = contentDisposition.match(/filename\*?=(?:UTF-8''|"?)([^";]+)/i);
    const rawHeaderFileName = fileNameMatch?.[1]?.trim() || '';
    const decodedHeaderFileName = decodeURIComponent(rawHeaderFileName.replace(/^"|"$/g, ''));
    const normalizedContentType = contentType.toLowerCase();
    const inferredExtension =
      normalizedContentType.includes('spreadsheetml') ||
      normalizedContentType.includes('xlsx') ||
      decodedHeaderFileName.toLowerCase().endsWith('.xlsx')
        ? '.xlsx'
        : '.csv';
    const fallbackNameWithExtension = /\.[A-Za-z0-9]+$/.test(fallbackFileName)
      ? fallbackFileName.replace(/\.[A-Za-z0-9]+$/, inferredExtension)
      : `${fallbackFileName}${inferredExtension}`;
    const decodedFileName = rawHeaderFileName ? decodedHeaderFileName : fallbackNameWithExtension;
    const fileName = /\.[A-Za-z0-9]+$/.test(decodedFileName)
      ? decodedFileName
      : `${decodedFileName}${inferredExtension}`;

    return { blob, fileName };
  }

    getSubDevAreaData(filters: subDevAreaTableData) {
    return this.http.post(this.subDevAreaTableUrl, filters).pipe(
      catchError((error) => {
        ErrorHandler.handleError(error, 'getSubDevAreaData');
        return throwError(() => new Error(ErrorHandler.extractApiErrorMessage(error)));
      })
    );
  }

  subDevAreaInsertUpdateRecord(requestBody: subDevAreaInsertUpdateRequest) {
    return this.http.post(this.subDevAreaInsertUpdateUrl, requestBody).pipe(
      map((response: any) => {
        const parsed = ErrorHandler.parseApiResult(response);
        return {
          statusCode: parsed.statusCode,
          statusMessage: parsed.message,
          error: !parsed.success,
          ...response,
        };
      }),
      catchError((error: any) => {
        const parsed = ErrorHandler.parseApiResult(error?.error || error);
        return throwError(() => ({
          statusCode: parsed.statusCode || error?.status || 500,
          statusMessage: parsed.message,
          error: true,
        }));
      })
    );
  }

  subDevAreaDeleteOilAndGasDetails(requestBody: subDevAreaDeleteRequest) {
    return this.http.post(this.subDevAreaDeleteUrl, requestBody).pipe(
      map((response: any) => {
        const parsed = ErrorHandler.parseApiResult(response);
        return {
          statusCode: parsed.statusCode,
          statusMessage: parsed.message,
          error: !parsed.success,
          ...response,
        };
      }),
      catchError((error: any) => {
        const parsed = ErrorHandler.parseApiResult(error?.error || error);
        return throwError(() => ({
          statusCode: parsed.statusCode || error?.status || 500,
          statusMessage: parsed.message,
          error: true,
        }));
      })
    );
  }


  getSubDevAreaFilterDropdownOptions(filters: subDevAreaFilterDropdowns) {
    return this.http.post(this.subDevAreaFilterDropdownUrl, filters).pipe(
      catchError((error) => {
        ErrorHandler.handleError(error, 'getSubDevAreaFilterDropdownOptions');
        return throwError(() => new Error(ErrorHandler.extractApiErrorMessage(error)));
      })
    );
  }

  getDropdownOptions(filters: adminDropdownoptions) {
    return this.http.post(this.dropdownUrl, filters).pipe(
      map((response: any) => response),
      catchError((error) => {
        ErrorHandler.handleError(error, 'getDropdownOptions');
        return throwError(() => new Error(ErrorHandler.extractApiErrorMessage(error)));
      })
    );
  }

  getTableData(filters: adminTableData) {
    return this.http.post(this.tableUrl, filters).pipe(
      catchError((error) => {
        ErrorHandler.handleError(error, 'getTableData');
        return throwError(() => new Error(ErrorHandler.extractApiErrorMessage(error)));
      })
    );
  }

  getFilterDropdownOptions(filters: adminDropdownFilters) {
    return this.http.post(this.filterDropdownUrl, filters).pipe(
      catchError((error) => {
        ErrorHandler.handleError(error, 'getFilterDropdownOptions');
        return throwError(() => new Error(ErrorHandler.extractApiErrorMessage(error)));
      })
    );
  }

  insertUpdateRecord(requestBody: adminInsertUpdateRequest) {
    return this.http.post(this.insertUpdateUrl, requestBody).pipe(
      map((response: any) => {
        const parsed = ErrorHandler.parseApiResult(response);
        return {
          statusCode: parsed.statusCode,
          statusMessage: parsed.message,
          error: !parsed.success,
          ...response,
        };
      }),
      catchError((error: any) => {
        const parsed = ErrorHandler.parseApiResult(error?.error || error);
        return throwError(() => ({
          statusCode: parsed.statusCode || error?.status || 500,
          statusMessage: parsed.message,
          error: true,
        }));
      })
    );
  }

  deleteOilAndGasDetails(requestBody: adminDeleteRequest) {
    return this.http.post(this.deleteUrl, requestBody).pipe(
      map((response: any) => {
        const parsed = ErrorHandler.parseApiResult(response);
        return {
          statusCode: parsed.statusCode,
          statusMessage: parsed.message,
          error: !parsed.success,
          ...response,
        };
      }),
      catchError((error: any) => {
        const parsed = ErrorHandler.parseApiResult(error?.error || error);
        return throwError(() => ({
          statusCode: parsed.statusCode || error?.status || 500,
          statusMessage: parsed.message,
          error: true,
        }));
      })
    );
  }
}
